import { format, isValid, parseISO, set } from 'date-fns';
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

export type DateInput = Date | string | number | null | undefined;

export const INVALID_DATE_LABEL = 'Invalid date';

/**
 * Parses a value the same way `moment(value)` did: `undefined` means "now",
 * `null` is invalid, and strings are read as ISO 8601 (date-only strings are
 * local midnight) before falling back to the native Date parser.
 */
export const toDate = (value: DateInput): Date => {
  if (value === undefined) return new Date();
  if (value === null) return new Date(NaN);
  if (value instanceof Date) return new Date(value.getTime());
  if (typeof value === 'number') return new Date(value);

  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : new Date(value);
};

const HAS_UTC_OFFSET = /(Z|[+-]\d{2}(:?\d{2})?)$/i;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Like toDate, but strings without an offset are read as UTC (as `moment.utc(value)` did). */
export const toUtcDate = (value: DateInput): Date => {
  if (typeof value !== 'string' || HAS_UTC_OFFSET.test(value.trim())) {
    return toDate(value);
  }
  const trimmed = value.trim();
  return toDate(DATE_ONLY.test(trimmed) ? `${trimmed}T00:00:00Z` : `${trimmed}Z`);
};

/** Formats a date-fns pattern, returning moment's "Invalid date" instead of throwing. */
export const formatDate = (value: DateInput, pattern: string): string => {
  const date = toDate(value);
  return isValid(date) ? format(date, pattern) : INVALID_DATE_LABEL;
};

/** Formats a number of seconds as a wrapping `HH:mm:ss` time of day. */
export const formatSecondsAsTime = (totalSeconds: number): string => {
  const seconds = Math.floor(((totalSeconds || 0) % 86400) + 86400) % 86400;
  const pad = (n: number) => String(n).padStart(2, '0');
  return [
    Math.floor(seconds / 3600),
    Math.floor((seconds % 3600) / 60),
    seconds % 60,
  ]
    .map(pad)
    .join(':');
};

/** An absolute instant paired with the IANA time zone it should be shown and edited in. */
export interface ZonedDateTime {
  date: Date;
  timeZone: string;
}

// Intl (CLDR) still reports some zones by IDs that IANA has since renamed.
const RENAMED_TIME_ZONES: Record<string, string> = {
  'Africa/Asmera': 'Africa/Asmara',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Catamarca': 'America/Argentina/Catamarca',
  'America/Coral_Harbour': 'America/Atikokan',
  'America/Cordoba': 'America/Argentina/Cordoba',
  'America/Godthab': 'America/Nuuk',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Jujuy': 'America/Argentina/Jujuy',
  'America/Louisville': 'America/Kentucky/Louisville',
  'America/Mendoza': 'America/Argentina/Mendoza',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Europe/Kiev': 'Europe/Kyiv',
  'Pacific/Enderbury': 'Pacific/Kanton',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Truk': 'Pacific/Chuuk',
};

export const toCurrentTimeZoneName = (timeZone: string): string =>
  RENAMED_TIME_ZONES[timeZone] ?? timeZone;

export const guessTimeZone = (): string =>
  toCurrentTimeZoneName(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  );

export const createZonedDateTime = (
  date: Date,
  timeZone: string = guessTimeZone(),
): ZonedDateTime => ({ date, timeZone });

export const isValidZonedDateTime = (value?: ZonedDateTime | null): boolean =>
  !!value && isValid(value.date);

/** A Date whose local fields equal the wall-clock time in the zone. */
export const getWallClock = ({ date, timeZone }: ZonedDateTime): Date =>
  utcToZonedTime(date, timeZone);

export const setWallClock = (
  value: ZonedDateTime,
  values: Parameters<typeof set>[1],
): ZonedDateTime => ({
  ...value,
  date: zonedTimeToUtc(set(getWallClock(value), values), value.timeZone),
});

/** Moves to another zone keeping the same wall-clock time (and so a different instant). */
export const changeTimeZoneKeepWallClock = (
  value: ZonedDateTime,
  timeZone: string,
): ZonedDateTime => ({
  date: zonedTimeToUtc(getWallClock(value), timeZone),
  timeZone,
});

export const formatZoned = (
  { date, timeZone }: ZonedDateTime,
  pattern: string,
): string => formatInTimeZone(date, timeZone, pattern);
